import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import type { MapRef, ViewStateChangeEvent } from 'react-map-gl/mapbox'
import Map, { Marker, NavigationControl, GeolocateControl } from 'react-map-gl/mapbox'
import styled from 'styled-components'
import { rem } from 'polished'
import { ChatMapContext, findMarkerByPlaceId } from './ChatMapContext'
import type { ChatMapMarker, ChatMapViewState } from './types'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import { MapboxGlobalStyles } from './styles'
import MapPitchToggleControl from './MapPitchToggleControl'
import MapStyleToggleControl from './MapStyleToggleControl'
import { getMapboxAccessToken, MAPBOX_ACCESS_TOKEN_USE_CASES } from 'lib/maps/mapboxAccessToken'
import {
  DEFAULT_MAP_STYLE_MODE,
  getMapboxConfig,
  getMapboxStyle,
  getMapPitchCamera,
  type MapPitchMode,
  type MapStyleMode,
} from './map3DConfig'
import { useMap3DStyle } from './useMap3DStyle'
import { useMapLightPreset } from './useMapLightPreset'

const DEFAULT_VIEW_STATE: ChatMapViewState = {
  longitude: 0,
  latitude: 20,
  zoom: 1.5,
}

const FOCUSED_ZOOM = 14

export interface MapMarkerRect {
  top: number
  bottom: number
  left: number
  right: number
  width: number
  height: number
}

interface ChatMapProps {
  markers: Array<ChatMapMarker>
  className?: string
  onMarkerClick?: (placeId: string, markerRect?: MapMarkerRect) => void
  activePlaceId?: string | null
  onActivePlaceIdChange?: (placeId: string | null) => void
  scrollZoom?: boolean
  initialCenterPlaceId?: string | null
  showGeolocateControl?: boolean
}

const Container = styled.div`
  width: 100%;
  height: 100%;
  min-height: ${rem(300)};
  position: relative;
`

const MarkerWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const MarkerContainer = styled.div<{ $active?: boolean; $secondary?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $secondary }) => $secondary ? rem(24) : rem(32)};
  height: ${({ $secondary }) => $secondary ? rem(24) : rem(32)};
  cursor: pointer;
  transform: translate(-50%, -100%);
  transition: transform 0.15s ease-out;

  ${({ $active }) =>
    $active &&
    `
    transform: translate(-50%, -100%) scale(1.2);
  `}
`

const MarkerPin = styled.div<{ $active?: boolean; $secondary?: boolean }>`
  width: ${({ $secondary }) => $secondary ? rem(20) : rem(28)};
  height: ${({ $secondary }) => $secondary ? rem(20) : rem(28)};
  border-radius: 50% 50% 50% 0;
  background-color: ${props =>
    props.$secondary
      ? props.theme.palette.neutral.surface.lightForegroundAlt
      : props.$active
        ? props.theme.palette.brand.primary.normal
        : props.theme.palette.neutral.surface.darkForeground};
  transform: rotate(-45deg);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 ${rem(2)} ${rem(6)} rgba(0, 0, 0, 0.3);
  transition: background-color 0.15s ease-out;

  svg {
    transform: rotate(45deg);
    color: ${props => props.theme.palette.neutral.surface.darkContrast};
  }
`

const MarkerLabel = styled.span`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  padding: ${rem(2)} ${rem(6)};
  background: rgba(255, 255, 255, 0.95);
  border-radius: ${rem(4)};
  font-size: ${rem(11)};
  font-weight: 600;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  box-shadow: 0 ${rem(1)} ${rem(4)} rgba(0, 0, 0, 0.15);
  white-space: nowrap;
  max-width: ${rem(100)};
  overflow: hidden;
  text-overflow: ellipsis;
`

function ChatMap({
  markers,
  className,
  onMarkerClick,
  activePlaceId: controlledActivePlaceId,
  onActivePlaceIdChange,
  scrollZoom = true,
  initialCenterPlaceId,
  showGeolocateControl = false,
}: ChatMapProps) {
  const intl = useIntl()
  const mapRef = useRef<MapRef>(null)
  const [viewState, setViewState] = useState<ChatMapViewState>(DEFAULT_VIEW_STATE)
  const [isMapLoaded, setIsMapLoaded] = useState(false)
  const [mapPitchMode, setMapPitchMode] = useState<MapPitchMode>('2d')
  const [mapStyleMode, setMapStyleMode] = useState<MapStyleMode>(DEFAULT_MAP_STYLE_MODE)
  const [internalActivePlaceId, setInternalActivePlaceId] = useState<string | null>(null)
  const hasInitialFitBounds = useRef(false)
  const shouldFlyToActivePlaceRef = useRef(true)
  const mapPitchModeRef = useRef(mapPitchMode)
  const lightPreset = useMapLightPreset({ enabled: mapStyleMode === DEFAULT_MAP_STYLE_MODE })
  useMap3DStyle({ mapRef, isMapLoaded, lightPreset })

  useEffect(() => {
    mapPitchModeRef.current = mapPitchMode
  }, [mapPitchMode])

  const activePlaceId = controlledActivePlaceId !== undefined
    ? controlledActivePlaceId
    : internalActivePlaceId

  const setActivePlaceId = useCallback((placeId: string | null) => {
    if (onActivePlaceIdChange) {
      onActivePlaceIdChange(placeId)
    } else {
      setInternalActivePlaceId(placeId)
    }
  }, [onActivePlaceIdChange])

  useEffect(() => {
    if (hasInitialFitBounds.current || markers.length === 0) {
      return
    }

    const map = mapRef.current?.getMap()
    if (!map) {
      return
    }

    hasInitialFitBounds.current = true

    const lngs = markers.map(m => m.lng)
    const lats = markers.map(m => m.lat)

    const minLng = Math.min(...lngs)
    const maxLng = Math.max(...lngs)
    const minLat = Math.min(...lats)
    const maxLat = Math.max(...lats)

    if (minLng === maxLng && minLat === maxLat) {
      setViewState({
        longitude: minLng,
        latitude: minLat,
        zoom: FOCUSED_ZOOM,
        ...getMapPitchCamera(mapPitchMode),
      })
      return
    }

    map.fitBounds(
      [[minLng, minLat], [maxLng, maxLat]],
      {
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
        maxZoom: 15,
        ...getMapPitchCamera(mapPitchMode),
        duration: 500,
      },
    )
  }, [mapPitchMode, markers])

  const focusPlace = useCallback((placeId: string) => {
    const marker = findMarkerByPlaceId(markers, placeId)
    if (!marker) {
      return
    }

    setViewState(prev => ({
      ...prev,
      longitude: marker.lng,
      latitude: marker.lat,
      zoom: FOCUSED_ZOOM,
      ...getMapPitchCamera(mapPitchModeRef.current),
    }))

    shouldFlyToActivePlaceRef.current = false
    setActivePlaceId(placeId)
  }, [markers, setActivePlaceId])

  useEffect(() => {
    if (!activePlaceId || !shouldFlyToActivePlaceRef.current) {
      shouldFlyToActivePlaceRef.current = true
      return
    }

    const marker = findMarkerByPlaceId(markers, activePlaceId)
    if (marker) {
      const map = mapRef.current?.getMap()
      if (map) {
        map.flyTo({
          center: [marker.lng, marker.lat],
          zoom: FOCUSED_ZOOM,
          ...getMapPitchCamera(mapPitchModeRef.current),
          duration: 500,
        })
      }
    }
  }, [activePlaceId, markers])

  const convertRectToMarkerRect = useCallback((rect?: DOMRect): MapMarkerRect | undefined => {
    if (!rect) {
      return undefined
    }
    return {
      top: rect.top,
      bottom: rect.bottom,
      left: rect.left,
      right: rect.right,
      width: rect.width,
      height: rect.height,
    }
  }, [])

  const handleMarkerClick = useCallback((placeId: string, markerRect?: DOMRect) => {
    shouldFlyToActivePlaceRef.current = false
    setActivePlaceId(placeId)
    onMarkerClick?.(placeId, convertRectToMarkerRect(markerRect))
  }, [convertRectToMarkerRect, onMarkerClick, setActivePlaceId])

  const initialCenterRef = useRef<string | null>(null)
  useEffect(() => {
    if (!initialCenterPlaceId) {
      return
    }
    if (initialCenterRef.current === initialCenterPlaceId) {
      return
    }
    initialCenterRef.current = initialCenterPlaceId
    focusPlace(initialCenterPlaceId)
  }, [initialCenterPlaceId, focusPlace])

  const handleMove = useCallback((evt: ViewStateChangeEvent) => {
    setViewState({
      longitude: evt.viewState.longitude,
      latitude: evt.viewState.latitude,
      zoom: evt.viewState.zoom,
      pitch: evt.viewState.pitch,
      bearing: evt.viewState.bearing,
    })
  }, [])

  const contextValue = useMemo(() => ({
    activePlaceId,
    setActivePlaceId,
    focusPlace,
    markers,
  }), [activePlaceId, setActivePlaceId, focusPlace, markers])

  const viewPlaceDetailsLabel = intl.formatMessage({
    id: 'XbK5JyHgvgONnlMFkuLT',
    defaultMessage: 'View place details',
    description: 'Fallback accessible label for a map marker button in the chat map',
  })

  const mapboxAccessToken = getMapboxAccessToken(MAPBOX_ACCESS_TOKEN_USE_CASES.tripPlanner)

  if (!mapboxAccessToken) {
    return (
      <Container className={className}>
        <div style={{ padding: 20, textAlign: 'center' }}>
          {intl.formatMessage({
            id: 'N1wlozfCxNO0uKUi1ATr',
            defaultMessage: 'Map unavailable - missing configuration',
            description: 'Fallback message for missing mapbox configuration in the trip planner AI chat',
          })}
        </div>
      </Container>
    )
  }

  return (
    <ChatMapContext.Provider value={contextValue}>
      <Container className={className}>
        <MapboxGlobalStyles />
        <Map
          ref={mapRef}
          mapboxAccessToken={mapboxAccessToken}
          {...viewState}
          onMove={handleMove}
          onLoad={() => setIsMapLoaded(true)}
          style={{ width: '100%', height: '100%' }}
          mapStyle={getMapboxStyle(mapStyleMode)}
          config={getMapboxConfig(lightPreset)}
          scrollZoom={scrollZoom}
          attributionControl={false}
          language={intl.locale}
          projection="globe"
        >
          {markers.map(marker => {
            const isSecondary = marker.variant === 'secondary'
            const isActive = marker.placeId === activePlaceId
            return (
              <Marker
                key={marker.placeId}
                longitude={marker.lng}
                latitude={marker.lat}
                anchor="bottom"
              >
                <MarkerWrapper>
                  <MarkerContainer
                    role="button"
                    tabIndex={0}
                    aria-label={marker.label ?? viewPlaceDetailsLabel}
                    $active={isActive}
                    $secondary={isSecondary}
                    onClick={(event) => {
                      event.stopPropagation()
                      handleMarkerClick(marker.placeId, event.currentTarget.getBoundingClientRect())
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        handleMarkerClick(marker.placeId, event.currentTarget.getBoundingClientRect())
                      }
                    }}
                  >
                    <MarkerPin $active={isActive} $secondary={isSecondary}>
                      <LineMapMarkerIcon size="XS" />
                    </MarkerPin>
                  </MarkerContainer>
                  {marker.label && <MarkerLabel>{marker.label}</MarkerLabel>}
                </MarkerWrapper>
              </Marker>
            )
          })}

          {NavigationControl && <NavigationControl position="bottom-right" showCompass visualizePitch />}
          <MapPitchToggleControl position="bottom-right" onPitchModeChange={setMapPitchMode} />
          <MapStyleToggleControl
            position="bottom-right"
            mapStyleMode={mapStyleMode}
            onMapStyleModeChange={setMapStyleMode}
          />
          {showGeolocateControl && GeolocateControl && <GeolocateControl position="bottom-right" />}
        </Map>
      </Container>
    </ChatMapContext.Provider>
  )
}

export default ChatMap
